import type React from 'react';
import {
  FaUsers,
  FaSearch,
  FaChevronLeft,
  FaChevronRight,
  FaArrowRight,
  FaBriefcase,
  FaBuilding,
  FaEnvelope,
  FaMapMarkerAlt,
  FaDollarSign,
  FaTimes,
  FaUserTie,
  FaClock,
  FaCheckCircle,
  FaTimesCircle,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { LeadApiResponse } from 'models/Lead';

import './LeadSearchModal.styles.scss';

type LeadsSearchModalProps = {
  isSearchDialogOpen: boolean;
  setIsSearchDialogOpen: (open: boolean) => void;
  totalItems: number;
  totalPages: number;
  currentPage: number;
  startIndex: number;
  endIndex: number;
  displayedItems: LeadApiResponse[];
  pageSize: number;
  handlePageChange: (page: number) => void;
  handlePageSizeChange: (value: number) => void;
  isLoading: boolean;
  modalSearchQuery: string;
  setModalSearchQuery: (value: string) => void;
  clearModalSearch: () => void;
  debouncedModalSearch: string;
};

const LeadsSearchModal: React.FC<LeadsSearchModalProps> = ({
  isSearchDialogOpen,
  setIsSearchDialogOpen,
  totalItems,
  totalPages,
  currentPage,
  startIndex,
  endIndex,
  displayedItems,
  handlePageChange,
  isLoading,
  modalSearchQuery,
  setModalSearchQuery,
  clearModalSearch,
  debouncedModalSearch,
}) => {
  const navigate = useNavigate();

  const highlightEmail = (email: string, search: string) => {
    if (!search.trim() || !email) return <span>{email}</span>;

    const parts = email.split(new RegExp(`(${search})`, 'gi'));
    return (
      <span>
        {parts.map((part, i) =>
          part.toLowerCase() === search.toLowerCase() ? (
            <mark key={i} className="search-highlight">
              {part}
            </mark>
          ) : (
            <span key={i}>{part}</span>
          ),
        )}
      </span>
    );
  };

  const getStatusBadge = (status: string) => {
    const statusClass = status.toLowerCase();
    const icons = {
      CONFIRMED: FaCheckCircle,
      PENDING: FaClock,
      REJECTED: FaTimesCircle,
    };
    const Icon = icons[status as keyof typeof icons] || icons.PENDING;

    return (
      <span className={`status-badge status-badge--${statusClass}`}>
        <Icon className="status-badge__icon" />
        {status}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (!isSearchDialogOpen) return null;

  return (
    <div className="leads-modal-overlay">
      <div className="modal">
        {/* Header */}
        <div className="modal__header">
          <div className="modal__header-content">
            <div className="modal__title-wrapper">
              <FaUsers className="modal__title-icon" />
              <h2 className="modal__title">Lead Search Results</h2>
            </div>
            <button
              onClick={() => setIsSearchDialogOpen(false)}
              className="modal__close-btn"
            >
              <FaTimes />
            </button>
          </div>
          <p className="modal__subtitle">
            Found {totalItems} lead{totalItems !== 1 ? 's' : ''} matching
            &quot;,
            {debouncedModalSearch}&quot;,
          </p>
        </div>

        {/* Search Bar */}
        <div className="modal__search">
          <div className="search-input">
            <FaSearch className="search-input__icon" />
            <input
              type="text"
              placeholder="Search by email..."
              value={modalSearchQuery}
              onChange={(e) => setModalSearchQuery(e.target.value)}
              className="search-input__field"
            />
            {modalSearchQuery && (
              <button
                onClick={clearModalSearch}
                className="search-input__clear"
              >
                <FaTimes />
              </button>
            )}
          </div>

          {!isLoading && (
            <p className="modal__info">
              Showing {startIndex} - {endIndex} of {totalItems} results
              {debouncedModalSearch && (
                <span className="modal__info--filtered">(filtered)</span>
              )}
            </p>
          )}
        </div>

        {/* Results */}
        <div className="modal__content">
          {isLoading ? (
            <div className="loading-state">
              <div className="loading-spinner" />
              <p className="loading-state__text">Loading results...</p>
            </div>
          ) : displayedItems?.length > 0 ? (
            <div className="leads-list">
              {displayedItems?.map((item) => (
                <div key={item._id} className="lead-card">
                  <div className="lead-card__main">
                    {/* Left side - Contact info */}
                    <div className="lead-card__left">
                      <div className="lead-card__name-section">
                        <h3 className="lead-card__name">
                          {item.lead.first_name} {item.lead.last_name}
                        </h3>
                        <div className="lead-card__badges">
                          {getStatusBadge(item.lead.leadStatus)}
                          {item.hasReplied && (
                            <span className="replied-badge">
                              <FaCheckCircle className="replied-badge__icon" />
                              REPLIED
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Email */}
                      <div className="lead-card__email">
                        <FaEnvelope className="lead-card__email-icon" />
                        <a
                          href={`mailto:${item.lead.email}`}
                          className="lead-card__email-link"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {highlightEmail(
                            item.lead.email,
                            debouncedModalSearch,
                          )}
                        </a>
                      </div>

                      {/* Secondary info */}
                      <div className="lead-card__details">
                        {item.lead.title && (
                          <div className="detail-item">
                            <FaUserTie className="detail-item__icon" />
                            <span className="detail-item__text">
                              {item.lead.title}
                            </span>
                          </div>
                        )}
                        <div className="detail-item">
                          <FaBuilding className="detail-item__icon" />
                          <span className="detail-item__text">
                            {item.companyName}
                          </span>
                        </div>
                        {item.location && item.location !== 'Not Found' && (
                          <div className="detail-item">
                            <FaMapMarkerAlt className="detail-item__icon" />
                            <span className="detail-item__text">
                              {item.location}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Right side - Position hiring for */}
                    <div className="lead-card__right">
                      <div className="position-tag">
                        <div className="position-tag__label">
                          <FaBriefcase className="position-tag__icon" />
                          <span>Hiring for</span>
                        </div>
                        <p className="position-tag__title">{item.jobTitle}</p>
                      </div>

                      <div className="lead-card__meta">
                        <span className="meta-item">
                          <FaClock className="meta-item__icon" />
                          {formatDate(item.lastEmailSent)}
                        </span>
                        {item.salary && item.salary !== 'Not found' && (
                          <span className="meta-item">
                            <FaDollarSign className="meta-item__icon" />
                            {item.salary}
                          </span>
                        )}
                      </div>

                      <button
                        onClick={() => {
                          setIsSearchDialogOpen(false);
                          navigate(`/lead/${item._id}`);
                        }}
                        className="lead-card__view-btn"
                      >
                        View Lead
                        <FaArrowRight />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <FaUsers className="empty-state__icon" />
              <p className="empty-state__text">
                No leads found matching your search
              </p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="modal__pagination">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1 || isLoading}
              className="pagination-btn"
            >
              <FaChevronLeft />
              Previous
            </button>

            <span className="pagination-info">
              Page {currentPage} of {totalPages}
            </span>

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages || isLoading}
              className="pagination-btn"
            >
              Next
              <FaChevronRight />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeadsSearchModal;
